import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";

import type {ChartOptions} from "chart.js";

import {getColor, type RevaChartStyle} from "../../util/chartUtils";

/**
 * Gemeinsame Kleinteile der Startseiten-Tabs (#19113). Die Statistik-Panels des Auftragspool- und
 * des Historie-Tabs (PoolStatisticsPanel/SnapshotStatisticsPanel) sollen exakt gleich hoch und
 * gleich gestaltet sein — deshalb liegen Kacheln, Legende, Chart-Optionen und Chart-Box-Höhe hier
 * zentral statt als handsynchronisierte Kopien in beiden Panels.
 */

/** Einheitliche Chart-Box beider Statistik-Panels — gleiche feste Höhe = gleiche Panel-Höhe. */
export const STATISTICS_CHART_BOX_CLASS = "w-full h-[16.2rem] bg-surface-0 dark:bg-surface-900";

/**
 * One summary number above the chart: the label sits right behind the big number to keep the tile
 * flat, optionally with a fine-print line below.
 */
export function StatTile({value, label, hint}: {value: string; label: string; hint?: string}) {
    return (
        <div className="flex flex-col px-4 py-1.5 rounded-lg bg-surface-100 dark:bg-surface-800">
            <span className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-surface-900 dark:text-surface-0">{value}</span>
                <span className="text-sm text-surface-500 dark:text-surface-300">{label}</span>
            </span>
            {hint && <span className="text-xs text-surface-400 dark:text-surface-400">{hint}</span>}
        </div>
    );
}

/** One entry of the HTML legend (or marker line): a colored square plus a label in text ink. */
export function LegendItem({color, label}: {color: string; label: string}) {
    return (
        <span className="flex items-center gap-1.5 text-sm text-surface-500 dark:text-surface-300">
            <span className="inline-block w-3 h-3 rounded-sm" style={{backgroundColor: color}} />
            {label}
        </span>
    );
}

/**
 * Tabellenzellen-Inhalt "farbiger Punkt + Label" — Status-Spalte der Bewertungslisten und
 * Typ-Spalte der Historie nutzen bewusst dieselbe Optik (Punktfarbe = Chart-/Checkbox-Farbe).
 */
export function ColorDotLabel({color, label}: {color: string; label: ReactNode}) {
    return (
        <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{backgroundColor: color}} />
            {label}
        </span>
    );
}

/** Eine Filter-Checkbox im Spaltenkopf, siehe {@link FilterCheckboxColumnHeader}. */
export interface FilterCheckboxItem {
    /** React-Key und zugleich Basis der inputId (muss stabil bleiben — die e2e-Tests adressieren sie). */
    inputId: string;
    color: string;
    checked: boolean;
    tooltip: string;
    onToggle: () => void;
}

/**
 * Spaltenkopf "Titel + farbige Filter-Checkboxen" (Status-Spalte der Bewertungslisten, Typ-Spalte
 * der Historie): keine Checkbox aktiv = alle Zeilen sichtbar, sonst nur die ausgewählten. Die
 * Checkbox ist in der Serien-/Statusfarbe eingefärbt (Rahmen immer, Füllung wenn aktiv, via
 * PrimeReact-`pt`); der Tooltip nennt den Namen des Status/Typs.
 */
export function FilterCheckboxColumnHeader({title, items}: {title: string; items: FilterCheckboxItem[]}) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <span>{title}</span>
            <div className="flex items-center gap-1">
                {items.map((item) => (
                    <Checkbox
                        key={item.inputId}
                        inputId={item.inputId}
                        checked={item.checked}
                        tooltip={item.tooltip}
                        tooltipOptions={{position: "top"}}
                        onChange={item.onToggle}
                        pt={{
                            box: {
                                style: {
                                    borderColor: item.color,
                                    backgroundColor: item.checked ? item.color : undefined,
                                },
                            },
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * Gemeinsame chart.js-Optionen der beiden Statistik-Balkendiagramme. Der Aufrufer memoized das
 * Ergebnis (useMemo auf chartStyle), damit die Chart-Instanz stabile Identitäten sieht.
 * @param chartStyle Theme-abhängige Farben (siehe getChartStyle)
 * @param autoSkipXTicks true = x-Achsen-Labels bei Platzmangel ausdünnen (Historie); false = alle
 *                       zeigen (Pool-Altersverteilung, wenige feste Buckets)
 */
export function buildStatisticsChartOptions(chartStyle: RevaChartStyle, autoSkipXTicks: boolean): ChartOptions<"bar"> {
    return {
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: "index",
            intersect: false,
        },
        scales: {
            x: {
                ticks: {color: getColor(chartStyle.ticks), maxRotation: 45, autoSkip: autoSkipXTicks},
                grid: {display: false},
            },
            y: {
                beginAtZero: true,
                grace: "10%",
                ticks: {color: getColor(chartStyle.ticks), precision: 0},
                grid: {color: getColor(chartStyle.grid)},
            },
        },
        plugins: {
            // Keine chart.js-Legende: die Panels rendern ihre Legende/Marker als HTML neben dem
            // Umschalter, um vertikalen Platz zu sparen.
            legend: {display: false},
        },
    };
}

/**
 * Statistik-Umschalter der Startseiten-Tabs (Auftragspool + eigene Historie): blendet das
 * zugehörige Statistik-Panel ein/aus. Sitzt in der {@link TabControlCluster} links vom Reload-Button,
 * damit der Reload-Button auf allen Tabs an derselben Stelle (ganz rechts) bleibt (#19113). Der
 * aufrufende Tab hält den Sichtbarkeits-State und rendert das Panel selbst; beim Einblenden feuert er
 * den Lade-Thunk (bei Cache-Hit ein no-op).
 */
export function StatisticsToggleButton({active, onToggle}: {active: boolean; onToggle: () => void}) {
    const {t} = useTranslation();
    return (
        <Button
            icon={PrimeIcons.CHART_BAR}
            rounded
            text
            outlined={active}
            aria-label={t("start.statistics.toggle")}
            tooltip={t("start.statistics.toggle")}
            tooltipOptions={{position: "top"}}
            onClick={onToggle}
        />
    );
}

/**
 * Einheitliche Kontroll-Ecke oben rechts über jedem Startseiten-Tab (#19113): der Reload-Button steht
 * IMMER ganz rechts (auf allen Tabs an derselben Stelle); ein optionaler Statistik-Umschalter sitzt
 * links davon. Wird als `action` in {@link TabHint} rechtsbündig einsortiert.
 */
export function TabControlCluster({statsToggle, onReload}: {statsToggle?: ReactNode; onReload: () => void}) {
    return (
        <div className="flex items-center gap-1">
            {statsToggle}
            <Button icon={PrimeIcons.REFRESH} onClick={onReload} text />
        </div>
    );
}
